/**
 * Smart API Response Cache
 * Caches API responses for faster page loads
 * Automatically expires old data
 */

class APICache {
  constructor(ttl = 5 * 60 * 1000) { // 5 minutes default
    this.cache = new Map();
    this.ttl = ttl;
  }

  /**
   * Generate cache key from request parameters
   */
  generateKey(endpoint, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    return `${endpoint}?${sortedParams}`;
  }

  /**
   * Get cached data if still valid
   */
  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    // Check if expired
    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return item.data;
  }

  /**
   * Store data in cache with expiry
   */
  set(key, data, ttl = this.ttl) {
    this.cache.set(key, {
      data,
      expiry: Date.now() + ttl,
      timestamp: Date.now()
    });
  }

  /**
   * Check if key exists and is valid
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Clear specific key
   */
  delete(key) {
    this.cache.delete(key);
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
  }

  /**
   * Clear expired entries
   */
  cleanup() {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (now > item.expiry) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Get cache stats
   */
  getStats() {
    const now = Date.now();
    let valid = 0;
    let expired = 0;
    
    for (const [, item] of this.cache.entries()) {
      if (now > item.expiry) {
        expired++;
      } else {
        valid++;
      }
    }
    
    return {
      total: this.cache.size,
      valid,
      expired
    };
  }
}

// Create singleton instances for different cache types
export const apiCache = new APICache(5 * 60 * 1000); // 5 min for API responses
export const dashboardCache = new APICache(2 * 60 * 1000); // 2 min for dashboard
export const campaignCache = new APICache(3 * 60 * 1000); // 3 min for campaigns

// Auto-cleanup every 5 minutes
setInterval(() => {
  apiCache.cleanup();
  dashboardCache.cleanup();
  campaignCache.cleanup();
}, 5 * 60 * 1000);

export default apiCache;

